import { type MouseEvent } from 'react';
import { type Bisector, type ContainerElement, max, values } from 'd3';
import { subDays, subHours, subMonths } from 'date-fns';

import { type DataPoint, type Series } from './types';

// Computes the maximum value for a series with multiple values per point
export function getDomainMaximum(series: Series) {
  return max(series, (d) => {
    const { time, ...vals } = d;
    return max<number>(values(vals));
  });
}

// Finds the data point that is closest to the mouse
export const resolveDataPoint = <U>(
  series: Series,
  scale: ((value: U) => number) & { invert: (value: number) => U },
  bisector: Bisector<DataPoint, U>['left'] | Bisector<DataPoint, U>['right'],
  accessor: (d: DataPoint) => U,
  element: ContainerElement | null | undefined,
  event: MouseEvent,
): DataPoint | null => {
  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  const point = [event.clientX - rect.left, event.clientY - rect.top];
  const x0 = scale.invert(point[0]);
  const index = bisector(series, x0, 1);
  const d0 = series[index - 1];
  const d1 = series[index];

  let d = d0;
  if (d1 && d1.time) {
    d = +x0 - +accessor(d0) > +accessor(d1) - +x0 ? d1 : d0;
  }

  return d;
};

export const today: Date = new Date();

const modify = (fn: (val: Date) => Date) => {
  const clone = (d: Date) => new Date(d);
  const val = clone(today);
  return fn(val);
};

const pastHour: Date = modify((d) => subHours(d, 1));
const past24Hours = modify((d) => subHours(d, 24));
const oneWeekAgo = modify((d) => subDays(d, 7));
const twoWeeksAgo = modify((d) => subDays(d, 14));
const oneMonthAgo = modify((d) => subMonths(d, 1));
const twoMonthsAgo = modify((d) => subMonths(d, 2));

const createShortcut = (label: string, dateRange: Date[]) => ({
  label,
  dateRange: [dateRange[0].getTime(), dateRange[1].getTime()] as [number, number],
});

export const createUsageShortcuts = () => [
  { last60Min: createShortcut('Last 60 minutes', [pastHour, today]) },
  { last24Hrs: createShortcut('Last 24 hours', [past24Hours, today]) },
  { last7Days: createShortcut('Last 7 days', [oneWeekAgo, today]) },
  { last14Days: createShortcut('Last 14 days', [twoWeeksAgo, today]) },
  { last30Days: createShortcut('Last 30 days', [oneMonthAgo, today]) },
  { last60Days: createShortcut('Last 60 days', [twoMonthsAgo, today]) },
];
